import * as React from 'react';
import { TextField, TextFieldProps, Grid } from '@mui/material';

/**
 * Props for {@link MTextRange}.
 */
interface MTextRangeProps extends Omit<TextFieldProps, 'onChange' | 'defaultValue' | 'name'> {
    /** Base OData field name; combined with `_ge`/`_le` suffixes when reporting changes. */
    name: string;
    /**
     * Called once per bound whenever either the "from" or "to" value changes.
     *
     * @param value - The new text value for the changed bound.
     * @param filterName - The OData field name with its `_ge`/`_le` suffix.
     */
    onChange: (value: string, filterName: string) => void;
    /** Initial `[from, to]` values. */
    defaultValue?: [string | undefined, string | undefined];
}

interface MTextRangeState {
    from?: string | null;
    to?: string | null;
}

/**
 * A pair of `TextField`s ("from" / "to") reporting each bound change
 * independently via `onChange`, suffixed with the `_ge`/`_le` OData operators.
 */
const MTextRange: React.FC<MTextRangeProps> = ({ name, onChange, defaultValue, ...props }) => {
    const [state, setState] = React.useState<MTextRangeState>({ from: defaultValue?.[0], to: defaultValue?.[1] });

    React.useEffect(() => {
        setState({ from: defaultValue?.[0] ?? null, to: defaultValue?.[1] ?? null });
    }, [defaultValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, label: 'from' | 'to', op: 'ge' | 'le') => {
        const { value } = e.target;
        setState((prev) => ({ ...prev, [label]: value }));
        onChange(value, `${name}_${op}`);
    };

    return (
        <Grid container>
            <Grid size={6}>
                <TextField {...props} defaultValue={state.from} onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement>, 'from', 'ge')} type="search" inputProps={{ autoComplete: 'on' }} />
            </Grid>
            <Grid size={6}>
                <TextField {...props} label="to" defaultValue={state.to} onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement>, 'to', 'le')} type="search" inputProps={{ autoComplete: 'on' }} />
            </Grid>
        </Grid>
    );
};

export default React.memo(MTextRange);
